import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  ChevronDown,
  X,
  Loader2,
  FileText,
  Calendar,
  MapPin,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getHireOrders, createHireOrder, deleteHireOrder, getAssets } from '../lib/api';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const styles = {
    confirmed: 'badge-info',
    dispatched: 'badge-warning',
    on_hire: 'badge-success',
    completed: 'badge-neutral',
    overdue: 'badge-error',
  };
  return (
    <span className={`${styles[status] || 'badge-neutral'} inline-block`}>
      {status.replace('_', ' ')}
    </span>
  );
};

export default function HireOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [formData, setFormData] = useState({
    client_name: '',
    site_address: '',
    start_date: '',
    end_date: '',
    notes: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  const fetchOrders = async () => {
    try {
      const params = {};
      if (search) params.client = search;
      if (statusFilter) params.status = statusFilter;
      
      const response = await getHireOrders(params);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load hire orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await getAssets({ status: 'available' });
      setAssets(response.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchAssets();
  }, [search, statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createHireOrder({
        ...formData,
        asset_ids: selectedAssets,
      });
      toast.success('Hire order created successfully');
      setDialogOpen(false);
      resetForm();
      fetchOrders();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create hire order');
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      site_address: '',
      start_date: '',
      end_date: '',
      notes: '',
    });
    setSelectedAssets([]);
  };

  const toggleAsset = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      await deleteHireOrder(orderToDelete.id);
      toast.success('Hire order deleted successfully');
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete hire order');
    }
  };

  const openDeleteDialog = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="hire-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl text-slate-900 uppercase tracking-tight">
            Hire Orders
          </h1>
          <p className="font-body text-slate-500 mt-1">
            {orders.length} orders total
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-primary hover:bg-primary-hover text-white font-heading font-bold uppercase tracking-wide shadow-industrial-sm"
              data-testid="create-order-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Hire Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl border-2 border-slate-200 shadow-industrial max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold uppercase">
                Create Hire Order
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="font-heading font-bold uppercase text-xs">Client Name</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                    className="mt-1 border-2 border-slate-200"
                    placeholder="ABC Builders Ltd"
                    data-testid="client-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-heading font-bold uppercase text-xs">Site Address</Label>
                  <Input
                    value={formData.site_address}
                    onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
                    required
                    className="mt-1 border-2 border-slate-200"
                    placeholder="123 Construction Way, London"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-heading font-bold uppercase text-xs">Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 border-2 border-slate-200"
                    placeholder="Access instructions, contact info, etc."
                  />
                </div>
              </div>

              {/* Asset Selection */}
              <div>
                <Label className="font-heading font-bold uppercase text-xs">Select Assets</Label>
                <div className="mt-2 border-2 border-slate-200 rounded max-h-48 overflow-y-auto">
                  {assets.length === 0 ? (
                    <p className="p-4 text-center text-slate-500 text-sm">No available assets</p>
                  ) : (
                    assets.map((asset) => (
                      <label
                        key={asset.id}
                        className={`flex items-center gap-3 p-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 ${
                          selectedAssets.includes(asset.asset_id) ? 'bg-primary/5' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAssets.includes(asset.asset_id)}
                          onChange={() => toggleAsset(asset.asset_id)}
                          className="w-4 h-4 text-primary rounded"
                        />
                        <div className="flex-1">
                          <p className="font-body font-semibold text-sm">{asset.name}</p>
                          <p className="font-mono text-xs text-slate-500">{asset.asset_id}</p>
                        </div>
                        <span className="badge-neutral text-xs">{asset.category}</span>
                      </label>
                    ))
                  )}
                </div>
                {selectedAssets.length > 0 && (
                  <p className="mt-2 text-sm text-slate-600">
                    {selectedAssets.length} asset(s) selected
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-primary hover:bg-primary-hover"
                  disabled={selectedAssets.length === 0}
                  data-testid="save-order-btn"
                >
                  Create Order
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filters */}
      <Card className="border-2 border-slate-200 shadow-industrial">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by client name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 border-2 border-slate-200"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48 h-12 border-2 border-slate-200">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
                <SelectItem value="on_hire">On Hire</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
          <FileText className="w-12 h-12 mb-2" />
          <p>No hire orders found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((order) => (
            <Card 
              key={order.id} 
              className="border-2 border-slate-200 shadow-industrial hover:shadow-industrial-dark transition-shadow"
              data-testid={`order-card-${order.order_number}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-mono text-sm text-primary font-bold">{order.order_number}</p>
                    <h3 className="font-heading font-bold text-lg mt-1">{order.client_name}</h3>
                  </div>
                  <StatusBadge status={order.status} />
                </div>
                
                <p className="font-body text-sm text-slate-600 mb-4">{order.site_address}</p>
                
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{order.start_date}</span>
                  </div>
                  <span>to</span>
                  <span>{order.end_date}</span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div>
                    <p className="font-mono text-xs text-slate-400">Items</p>
                    <p className="font-heading font-bold">{order.items?.length || 0}</p>
                  </div>
                  <div>
                    <p className="font-mono text-xs text-slate-400">Value</p>
                    <p className="font-heading font-bold">£{order.total_value?.toFixed(2)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/hire-orders/${order.order_number}`}>
                      <Button variant="outline" size="sm" className="border-2 border-slate-200">
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-2 border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => openDeleteDialog(order)}
                      data-testid={`delete-order-${order.order_number}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-2 border-slate-200 shadow-industrial">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-bold uppercase">
              Delete Hire Order
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Are you sure you want to delete hire order{' '}
              <span className="font-mono font-bold text-primary">
                {orderToDelete?.order_number}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-2 border-slate-200">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrder}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-order"
            >
              Delete Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
